import { type SwissReductionCard } from "../types/cff-pricing.types";

const SBB_CH_EN_SEARCH_BASE = "https://www.sbb.ch/en";

const DEFAULT_SBB_REDUCTION: SwissReductionCard = "none";

/**
 * Resolves a short station / place label from a leg endpoint when segmentation names are absent.
 *
 * @param step - Leg endpoint (`from` / `to`) with optional structured stop name.
 * @returns Trimmed display label, or empty string when unavailable.
 */
export const itineraryLegEndpointDisplayLabel = ( step: {
    readonly "name"?: string;
    readonly "stop"?: { readonly "name"?: string };
} | undefined ): string => {
    if ( step === undefined ) {
        return "";
    }

    const stopName = typeof step.stop?.name === "string" ? step.stop.name.trim() : "";
    if ( stopName.length > 0 ) {
        return stopName;
    }

    return typeof step.name === "string" ? step.name.trim() : "";
};

/**
 * Maps a departure instant to sbb.ch query pieces using the Europe/Zurich civil calendar and clock
 * (matches typical SBB timetable expectations for Swiss journeys).
 *
 * @param departureTimeIso - Segment or leg departure time in ISO-8601.
 * @returns `day` as `YYYY-MM-DD` and `timeWithUnderscore` as `HH_mm`, or `null` when parsing fails.
 */
const formatZurichDayAndTimeForSbb = ( departureTimeIso: string ): {
    "day":                string;
    "timeWithUnderscore": string;
} | null => {
    const instant = new Date( departureTimeIso );
    if ( Number.isNaN( instant.getTime() ) ) {
        return null;
    }

    const parts = new Intl.DateTimeFormat( "en-GB", {
        "timeZone": "Europe/Zurich",
        "year":     "numeric",
        "month":    "2-digit",
        "day":      "2-digit",
        "hour":     "2-digit",
        "minute":   "2-digit",
        "hour12":   false,
    } ).formatToParts( instant );

    const pick = ( type: Intl.DateTimeFormatPartTypes ): string => parts.find( ( p ) => p.type === type )?.value ?? "";
    const year = pick( "year" );
    const month = pick( "month" );
    const day = pick( "day" );
    const hourRaw = pick( "hour" );
    const minuteRaw = pick( "minute" );

    if ( year === "" || month === "" || day === "" || hourRaw === "" || minuteRaw === "" ) {
        return null;
    }

    const hour = hourRaw.padStart( 2, "0" );
    const minute = minuteRaw.padStart( 2, "0" );

    return {
        "day":                `${ year }-${ month }-${ day }`,
        "timeWithUnderscore": `${ hour }_${ minute }`,
    };
};

/**
 * Builds the sbb.ch `/en` "stations" search URL (`stops`, `day`, `time`, `moment=dep`, `reduction`) used when
 * FlowRide cannot return a priced Swiss CFF offer but the user should still reach SBB booking search.
 *
 * @param input - Endpoint labels, departure time, and Swiss reduction profile (`none`, `half-fare`, `ga`).
 * @returns Absolute URL, or `null` when labels or time cannot produce a valid query.
 * @remarks Uses {@link URLSearchParams} for encoding; SBB tolerates percent-encoded punctuation in practice.
 */
export const buildSbbChEnStationsSearchUrl = ( input: {
    readonly "fromStopLabel":    string;
    readonly "toStopLabel":      string;
    readonly "departureTimeIso": string;
    readonly "reduction"?:       SwissReductionCard;
} ): string | null => {
    const from = input.fromStopLabel.trim();
    const to = input.toStopLabel.trim();
    if ( from === "" || to === "" ) {
        return null;
    }

    const zurich = formatZurichDayAndTimeForSbb( input.departureTimeIso );
    if ( zurich === null ) {
        return null;
    }

    const url = new URL( SBB_CH_EN_SEARCH_BASE );
    url.searchParams.set( "stops", `${ from }~${ to }` );
    url.searchParams.set( "day", zurich.day );
    url.searchParams.set( "time", zurich.timeWithUnderscore );
    url.searchParams.set( "moment", "dep" );
    url.searchParams.set( "reduction", input.reduction ?? DEFAULT_SBB_REDUCTION );

    return url.toString();
};
